// Proposed law: the UK Cyber Security and Resilience Bill, as a readiness view on the Legal page.
// It is not law: nothing here is an obligation, a score, a count or legal advice, and the Legal
// page's summary and groups never see it. Rules on the server in backend/src/lib/csrBillReadiness.ts;
// contract in docs/CSR-BILL-READINESS.md. GET /proposed-laws/csr-bill answers 404 when the
// organisation's Operating Locations do not include the UK, and the panel then shows nothing.
// Answers need legal:write or controls:write (the server decides; a 403 leaves it read-only).

import { useEffect, useState } from "react";
import { ExternalLink, Info } from "lucide-react";
import { api, ApiError, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DimensionHeading, SheetCard, StatusTag, type StatusTone } from "./Drawing";
import { theme, inputStyle, type as text } from "../theme";

type Tri = "yes" | "no" | "unknown";
type Readiness = "prepared" | "partly" | "not_prepared";

interface CsrBillView {
  regulation: string;
  statusNote: string;
  sourceUrl: string;
  questions: Array<{ key: string; text: string; answer: boolean | null }>;
  ukNisStatus: "required" | "conditional" | "exempt" | "unknown" | "not_in_scope";
  provisions: Array<{
    key: string;
    title: string;
    summary: string;
    applies: Tri;
    recordsLevel: Readiness | null;
    recordLines: string[];
    selfAssessment: Readiness | null;
    action: string;
  }>;
  likelyInScope: Tri;
}

const READINESS_LABEL: Record<Readiness, string> = {
  prepared: "Prepared",
  partly: "Partly prepared",
  not_prepared: "Not prepared",
};
const READINESS_TONE: Record<Readiness, StatusTone> = { prepared: "met", partly: "partial", not_prepared: "gap" };

const APPLIES_LABEL: Record<Tri, string> = { yes: "Would apply to you", no: "Would not apply to you", unknown: "Not yet clear" };

const REACH: Record<Tri, { tone: StatusTone; label: string; detail: string }> = {
  yes: { tone: "partial", label: "Likely", detail: "On your answers and records, the Bill as introduced would reach you." },
  no: { tone: "met", label: "Unlikely", detail: "On your answers and records, the Bill as introduced would not reach you." },
  unknown: { tone: "none", label: "Not yet clear", detail: "Answer the questions above to see whether the Bill as introduced would reach you." },
};

const NIS_STATUS: Record<CsrBillView["ukNisStatus"], string> = {
  required: "The UK NIS Regulations, which the Bill amends, already apply to you on your threshold profile.",
  conditional: "Whether the UK NIS Regulations, which the Bill amends, apply to you depends on a condition in your threshold profile.",
  exempt: "The UK NIS Regulations, which the Bill amends, do not apply to you on your threshold profile today.",
  unknown: "Whether the UK NIS Regulations, which the Bill amends, apply to you is not yet known: complete the threshold profile above.",
  not_in_scope: "The UK NIS Regulations, which the Bill amends, are not among the laws in your Operating Locations.",
};

export function CsrBillReadiness() {
  const { user } = useCurrentUser();
  const mayAnswer = !!user?.permissions?.some((p) => p === "legal:write" || p === "controls:write");
  const [view, setView] = useState<CsrBillView | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.get<CsrBillView>("/proposed-laws/csr-bill")
      .then((v) => { if (live) setView(v); })
      .catch((e) => {
        if (!live) return;
        // 404: the UK is not in the Operating Locations, so the Bill is not in view at all.
        if (e instanceof ApiError && e.status === 404) setHidden(true);
        else setLoadError(apiErrorMessage(e, "Could not load the Cyber Security and Resilience Bill readiness"));
      });
    return () => { live = false; };
  }, []);

  if (hidden || (!view && !loadError)) return null;

  const editable = mayAnswer && !readOnly;

  async function save(key: string, body: { scope?: Record<string, boolean | null>; selfAssessment?: Record<string, Readiness | null> }) {
    setSaving(key);
    setError(null);
    try {
      setView(await api.put<CsrBillView>("/proposed-laws/csr-bill", body));
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setReadOnly(true);
        setError("Only members who record legal or control work can change these answers.");
      } else {
        setError(apiErrorMessage(e, "Could not save the answer"));
      }
    } finally {
      setSaving(null);
    }
  }

  return (
    <section style={{ margin: "24px 0" }} aria-label="Proposed law readiness">
      <DimensionHeading>Proposed law: Cyber Security and Resilience Bill — readiness</DimensionHeading>
      {!view ? (
        <SheetCard><div style={{ ...text.hint, color: theme.color.dangerText }}>{loadError}</div></SheetCard>
      ) : (
        <SheetCard label={view.regulation} reference="Not law · not scored">
          {/* The status note leads: this is a Bill, not a law */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", border: `1px solid ${theme.color.warning}`, background: theme.color.warningBg, borderRadius: 3, marginBottom: 14 }}>
            <Info size={16} color={theme.color.warningText} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ ...text.body, fontWeight: 600, color: theme.color.warningText }}>{view.statusNote}</div>
              <div style={{ ...text.hint, marginTop: 4 }}>
                It is not counted in your obligations or scores above.{" "}
                <a href={view.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.ref, display: "inline-flex", alignItems: "center", gap: 3 }}>
                  The Bill on the UK Parliament website <ExternalLink size={11} />
                </a>
              </div>
            </div>
          </div>

          <div style={{ ...text.eyebrow, marginBottom: 8 }}>Would it reach you?</div>
          <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
            {view.questions.map((q) => (
              <div key={q.key} style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
                <div style={{ ...text.body, flex: "1 1 320px", minWidth: 0 }}>{q.text}</div>
                {editable ? (
                  <div role="radiogroup" aria-label={q.text} style={{ display: "flex", flexShrink: 0 }}>
                    {([[true, "Yes"], [false, "No"], [null, "Not sure"]] as const).map(([value, label], i) => {
                      const on = q.answer === value;
                      return (
                        <button
                          key={label} type="button" role="radio" aria-checked={on} disabled={saving === q.key}
                          onClick={() => { if (!on) void save(q.key, { scope: { [q.key]: value } }); }}
                          style={{
                            fontSize: 12, padding: "5px 12px", cursor: "pointer", fontWeight: on ? 600 : 400,
                            border: `1px solid ${on ? theme.color.accent : theme.color.border}`, marginLeft: i === 0 ? 0 : -1,
                            background: on ? theme.color.accentBg : "transparent", color: on ? theme.color.accentText : theme.color.muted,
                            borderRadius: i === 0 ? "3px 0 0 3px" : i === 2 ? "0 3px 3px 0" : 0, position: on ? "relative" : undefined,
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <span style={{ ...text.body, fontWeight: 600, flexShrink: 0 }}>{q.answer === true ? "Yes" : q.answer === false ? "No" : "Not answered"}</span>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", background: theme.color.panelAlt, borderRadius: 3, marginBottom: 16 }}>
            <StatusTag tone={REACH[view.likelyInScope].tone}>{REACH[view.likelyInScope].label}</StatusTag>
            <div>
              <div style={text.body}>{REACH[view.likelyInScope].detail}</div>
              <div style={{ ...text.hint, marginTop: 2 }}>{NIS_STATUS[view.ukNisStatus]}</div>
            </div>
          </div>

          <div style={{ ...text.eyebrow, marginBottom: 8 }}>What the Bill would change</div>
          <div>
            {view.provisions.map((p) => (
              <div key={p.key} style={{ borderTop: `1px solid ${theme.color.border}`, padding: "12px 0", display: "grid", gap: 6, opacity: p.applies === "no" ? 0.75 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                  <div style={text.itemTitle}>{p.title}</div>
                  <span style={{ ...text.meta, fontWeight: 700, padding: "1px 7px", borderRadius: 2, border: `1px solid ${p.applies === "yes" ? theme.color.borderStrong : theme.color.border}`, color: p.applies === "yes" ? theme.color.heading : theme.color.subtle }}>
                    {APPLIES_LABEL[p.applies]}
                  </span>
                </div>
                <div style={text.hint}>{p.summary}</div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(180px, 220px)", gap: 12, alignItems: "start" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ ...text.label, marginBottom: 0 }}>What your records show</span>
                      {p.recordsLevel
                        ? <StatusTag tone={READINESS_TONE[p.recordsLevel]}>{READINESS_LABEL[p.recordsLevel]}</StatusTag>
                        : <StatusTag tone="none">Nothing to go on yet</StatusTag>}
                    </div>
                    {p.recordLines.length > 0 && (
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18, ...text.hint }}>
                        {p.recordLines.map((l) => <li key={l}>{l}</li>)}
                      </ul>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`csr-${p.key}`} style={text.label}>Your own readiness</label>
                    {editable ? (
                      <select
                        id={`csr-${p.key}`} value={p.selfAssessment ?? ""} disabled={saving === p.key}
                        onChange={(e) => void save(p.key, { selfAssessment: { [p.key]: (e.target.value || null) as Readiness | null } })}
                        style={{ ...inputStyle, padding: "6px 10px" }}
                      >
                        <option value="">Not assessed</option>
                        {(Object.keys(READINESS_LABEL) as Readiness[]).map((r) => <option key={r} value={r}>{READINESS_LABEL[r]}</option>)}
                      </select>
                    ) : (
                      <div id={`csr-${p.key}`} style={text.body}>{p.selfAssessment ? READINESS_LABEL[p.selfAssessment] : "Not assessed"}</div>
                    )}
                  </div>
                </div>
                <div style={text.hint}><strong style={{ color: theme.color.text }}>Next step:</strong> {p.action}</div>
              </div>
            ))}
          </div>

          {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
          {!editable && !error && <div style={{ ...text.meta, marginTop: 8 }}>Members who record legal or control work can answer these questions.</div>}
        </SheetCard>
      )}
    </section>
  );
}
